import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router-dom";
import { clearEvent, getEvent } from '../../store/ducks/event/actions';
import { hideFixedFooterContent, hideMobileOptions, showMobileOptions } from 'store/ducks/nav/actions';
import { EventState } from 'store/ducks/event/types';
import PageNotFoundMessage from 'components/pageNotFound/PageNotFoundMessage/PageNotFoundMessage';
import { RenderIf } from 'components/layout';
import { loadRemote } from 'utils/moduleFederation';
import {
	EventSeatsContainer,
	EventSeatsMobileNavBar,
	NavEventTitle,
	NavIconContainer,
	NavTitle,
	NavTitleContainer,
	SeatsMapContainer,
	SeatsMapContentContainer, NavBarContent
} from "./styles";
import { ArrowLeft } from "react-feather";
import Skeleton from "react-loading-skeleton";
import { useEvent } from "./useEvent";
import { useSeatsMapAddToCart } from "./useSeatsMapAddToCart";
import { useEventMarketing } from "./useEventMarketing";
import FacebookPixelManagement from "components/layout/facebookPixelManagement/FacebookPixelManagement";
import TiktokPixelManagement from "components/layout/tiktokPixelManagment/TiktokPixelManagment";
import LoadingSpinner from "../../components/event/LoadingSpinner/LoadingSpinner";

interface StateProps {
	event: EventState,
}

const EventGeneralAdmissionPage = () => {
	const { url, eventDate } = useParams<{ url: string, eventDate: string }>();
	const select = (state: StateProps) => state.event;
	const dispatch = useDispatch();
	const { data, error, loading } = useSelector(select);
	const { handleCheckout } = useEvent(Number(data.id));
	const handleAddTicket = useSeatsMapAddToCart();
	const dataMarketing = useEventMarketing(data.id);
	const history = useHistory();

	const _navTitle = 'Sem lugar marcado';
	const seatsMapContainerRef = React.useRef<HTMLDivElement>(null);

	const [libLoaded, setLibLoaded] = useState(false);

	useEffect(() => {
		dispatch(clearEvent());
		dispatch(hideMobileOptions());
		dispatch(hideFixedFooterContent());
	}, []);

	useEffect(() => {
		dispatch(getEvent(url));
	}, [dispatch]);

	const loadMap = () => {
		if (data.seatsMapActived && !customElements.get('seats-map-element')) {
			const remoteUrl = process.env.REACT_APP_SEATS_MAP_MF_URL || 'http://localhost:5001/assets/remoteEntry.js';

			loadRemote(remoteUrl)
				.then(() => {
					setTimeout(() => {
						setLibLoaded(true);
					}, 600)
				})
				.catch(err => alert(`EventGeneralAdmissionPage: Failed to load Seats Map Microfrontend: ${err}`));
		} else if (customElements.get('seats-map-element')) {
			setLibLoaded(true);
		}
	}

	useEffect(() => {
		if (data.url == url && data.hasOutsideMapSectors) {
			loadMap();
		}
	}, [data.url]);

	useEffect(() => {
		if (!libLoaded) return;

		const seatsMapElement =
			seatsMapContainerRef.current?.querySelector('seats-map-element');

		if (!seatsMapElement) return;

		const checkoutEventListener = () => {
			handleCheckout();
		};

		seatsMapElement.addEventListener('checkout-event', checkoutEventListener);
		seatsMapElement.addEventListener('add-ticket', handleAddTicket);

		return () => {
			seatsMapElement.removeEventListener('checkout-event', checkoutEventListener);
			seatsMapElement.removeEventListener('add-ticket', handleAddTicket);
		};
	}, [libLoaded, handleCheckout, handleAddTicket]);

	useEffect(() => {
		return () => {
			dispatch(showMobileOptions());
			dispatch(hideFixedFooterContent());
			dispatch(clearEvent());
		}
	}, []);

	const getLoading = () => {
		if (loading && !error) {
			return (<LoadingSpinner />)
		}
	}

	const handleBack = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			history.push(`/${url}`);
		}
	};

	return (<>
		{error ? <PageNotFoundMessage /> : (<>
			{dataMarketing && <>
				<FacebookPixelManagement data={dataMarketing} eventId={data.id} />
				<TiktokPixelManagement data={dataMarketing} eventId={data.id} />
			</>}
			<EventSeatsContainer>
				<EventSeatsMobileNavBar>
					<NavBarContent>
						<NavIconContainer onClick={handleBack}><ArrowLeft /></NavIconContainer>
						<NavTitleContainer>
							{loading ? <Skeleton height={20} width={150} /> :
								<NavEventTitle>{data.name}</NavEventTitle>}
							<NavTitle>{_navTitle}</NavTitle>
						</NavTitleContainer>
					</NavBarContent>
				</EventSeatsMobileNavBar>
				{loading ? getLoading() : (<>
					<SeatsMapContainer>
						<RenderIf condition={data.hasOutsideMapSectors}>
							<SeatsMapContentContainer ref={seatsMapContainerRef}>
								{libLoaded && (<seats-map-element
									tenant-id={process.env.REACT_APP_TENANT_ID}
									event-id={data.id}
									event-dates={JSON.stringify(data.eventDates)}
									event-date-id={Number(eventDate)}
									view-mode="VIEWER"
									stage-width={seatsMapContainerRef?.current?.clientWidth}
									stage-height={800}
									has-outside-map-sectors="true"
									initial-mode="outside"
								/>)}
							</SeatsMapContentContainer>
						</RenderIf>
					</SeatsMapContainer>
				</>)}
			</EventSeatsContainer>
		</>)}
	</>);
};

export default EventGeneralAdmissionPage;
